import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Star, Building, CheckCircle, FileText, Calendar, User, MessageSquare, Wand2, Edit3, Send, Sparkles, Clock, Award, Eye, Heart, Share2, Edit, AlertTriangle, Target } from 'lucide-react';

interface UseCase {
  id: string;
  promoterName: string;
  promoterCompany: string;
  promoterAvatar: string;
  title?: string;
  content?: string;
  submissionDate?: string;
  publishDate?: string;
  featuredDate?: string;
  status: string;
  product: string;
  industry?: string;
  companySize?: string;
  notes?: string;
  quality?: string;
  views?: number;
  downloads?: number;
  shares?: number;
  likes?: number;
  performance?: string;
  featured?: boolean;
  companyResponse?: string;
  type?: string;
  benefits?: string[];
  icon?: string;
  color?: string;
  description?: string;
}

const PublishedUseCaseDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [showResponseSection, setShowResponseSection] = useState(false);
  const [responseMode, setResponseMode] = useState<'ai' | 'manual'>('ai');
  const [responseText, setResponseText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isRequestingEdit, setIsRequestingEdit] = useState(false);
  const [isMakingFeatured, setIsMakingFeatured] = useState(false);

  const useCase: UseCase = {
    id: id || '1',
    promoterName: 'Marie Leroy',
    promoterCompany: 'Digital Agency',
    promoterAvatar: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150',
    title: 'Increasing Marketing ROI by 300%',
    content: 'Using Clareefai, we transformed our digital marketing strategy and saw incredible results in the first 6 months.',
    submissionDate: '2024-01-20',
    publishDate: '2024-01-25',
    status: 'published',
    product: 'Clareefai Platform',
    industry: 'Marketing',
    companySize: '11-50 employees',
    quality: 'high',
    views: 1254,
    downloads: 87,
    shares: 34,
    likes: 156,
    performance: 'excellent',
    benefits: ['300% ROI increase', 'Reduced costs by 40%', 'Better customer targeting'],
    notes: 'Excellent case study, high engagement',
    type: 'promoter_submission'
  };

  useEffect(() => {
    if (useCase) {
      setResponseText(useCase.companyResponse || '');
    }
  }, [useCase]);

  const handleClose = () => {
    navigate('/use-cases');
  };

  const generateAIResponse = async () => {
    if (!useCase) return;

    setIsGenerating(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    const aiResponse = `Thank you ${useCase.promoterName} for this excellent use case! We're delighted that ${useCase.product} has delivered such impressive results for ${useCase.promoterCompany}. Your detailed case study will be incredibly valuable for our community and will help other companies understand the real-world benefits of our solution. We look forward to continuing our partnership and achieving even greater success together!`;

    setResponseText(aiResponse);
    setIsGenerating(false);
  };

  const handleSaveResponse = () => {
    if (!useCase) return;

    console.log('Saving response:', responseText);
    setShowResponseSection(false);
  };

  const handlePublish = async () => {
    if (!useCase) return;

    setIsPublishing(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    console.log('Publishing use case');

    setIsPublishing(false);
    handleClose();
  };

  const handleMakeFeatured = async () => {
    if (!useCase) return;

    setIsMakingFeatured(true);

    await new Promise(resolve => setTimeout(resolve, 1500));

    console.log('Making featured');

    setIsMakingFeatured(false);
  };

  const handleRequestEdit = async () => {
    if (!useCase) return;

    setIsRequestingEdit(true);

    await new Promise(resolve => setTimeout(resolve, 1500));

    console.log('Requesting edit');

    setIsRequestingEdit(false);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      editing_required: 'bg-orange-100 text-orange-800',
      rejected: 'bg-red-100 text-red-800',
      published: 'bg-[#4339A0] text-white',
      featured: 'bg-yellow-100 text-yellow-800'
    };

    const labelKeys: Record<string, string> = {
      pending_review: 'useCases.status.pendingReview',
      approved: 'useCases.status.approved',
      editing_required: 'useCases.status.editingRequired',
      rejected: 'useCases.status.rejected',
      published: 'useCases.status.published',
      featured: 'useCases.status.featured'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labelKeys[status] ? t(labelKeys[status]) : status}
      </span>
    );
  };

  const getQualityBadge = (quality?: string) => {
    if (!quality) return null;

    const badges = {
      high: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-red-100 text-red-800'
    };

    const labelKeys: Record<string, string> = {
      high: 'useCases.publishedDetail.qualityHigh',
      medium: 'useCases.publishedDetail.qualityMedium',
      low: 'useCases.publishedDetail.qualityLow'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[quality as keyof typeof badges]}`}>
        {labelKeys[quality] ? t(labelKeys[quality]) : quality}
      </span>
    );
  };

  const getPerformanceBadge = (performance?: string) => {
    if (!performance) return null;

    const badges = {
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      average: 'bg-yellow-100 text-yellow-800'
    };

    const labelKeys: Record<string, string> = {
      excellent: 'useCases.publishedDetail.performanceExcellent',
      good: 'useCases.publishedDetail.performanceGood',
      average: 'useCases.publishedDetail.performanceAverage'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[performance as keyof typeof badges]}`}>
        {labelKeys[performance] ? t(labelKeys[performance]) : performance}
      </span>
    );
  };

  const isPromoterSubmission = useCase?.type === 'promoter_submission' ||
                              ['pending_review', 'approved', 'editing_required', 'rejected'].includes(useCase?.status || '');

  if (!useCase) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-sm">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-[#4339A0] to-indigo-500 text-white rounded-t-xl">
            <h2 className="text-xl font-semibold">
              {isPromoterSubmission ? t('useCases.publishedDetail.submissionTitle') : t('useCases.publishedDetail.title')}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 150px)' }}>
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('useCases.publishedDetail.promoterInfo')}</h3>
              <div className="flex items-center space-x-4">
                <img
                  src={useCase.promoterAvatar}
                  alt={useCase.promoterName}
                  className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                />
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-900">{useCase.promoterName}</h4>
                  <p className="text-gray-600 flex items-center mt-1">
                    <Building className="w-4 h-4 mr-1" />
                    {useCase.promoterCompany}
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      {useCase.submissionDate && new Date(useCase.submissionDate).toLocaleDateString('en-US')}
                    </div>
                    {useCase.industry && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Target className="w-4 h-4 mr-1" />
                        {useCase.industry}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {getStatusBadge(useCase.status)}
                  {useCase.quality && (
                    <div className="mt-2">
                      {getQualityBadge(useCase.quality)}
                    </div>
                  )}
                  {useCase.performance && (
                    <div className="mt-2">
                      {getPerformanceBadge(useCase.performance)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {useCase.title && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-[#4339A0]" />
                  {t('useCases.publishedDetail.useCaseContent')}
                </h4>

                <div className="space-y-4">
                  <div>
                    <h5 className="font-semibold text-gray-900 text-xl mb-2">{useCase.title}</h5>
                    {useCase.content && (
                      <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-[#4339A0]">
                        <p className="text-gray-800 leading-relaxed">{useCase.content}</p>
                      </div>
                    )}
                  </div>

                  {useCase.benefits && useCase.benefits.length > 0 && (
                    <div>
                      <h6 className="font-medium text-gray-900 mb-3">{t('useCases.publishedDetail.keyBenefits')}</h6>
                      <div className="grid grid-cols-2 gap-2">
                        {useCase.benefits.map((benefit, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2 p-2 bg-green-50 border border-green-200 rounded-lg"
                          >
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-green-800">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">{t('useCases.fields.product')}:</span>
                    <p className="text-gray-900">{useCase.product}</p>
                  </div>
                  {useCase.industry && (
                    <div>
                      <span className="font-medium text-gray-600">{t('useCases.fields.industry')}:</span>
                      <p className="text-gray-900">{useCase.industry}</p>
                    </div>
                  )}
                  {useCase.companySize && (
                    <div>
                      <span className="font-medium text-gray-600">{t('useCases.fields.companySize')}:</span>
                      <p className="text-gray-900">{useCase.companySize}</p>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-600">{t('useCases.fields.status')}:</span>
                    <div className="mt-1">{getStatusBadge(useCase.status)}</div>
                  </div>
                </div>

                {useCase.views !== undefined && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <Eye className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <div className="text-lg font-bold text-blue-900">{useCase.views.toLocaleString()}</div>
                      <div className="text-xs text-blue-700">{t('useCases.publishedDetail.views')}</div>
                    </div>
                    {useCase.downloads !== undefined && (
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <FileText className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                        <div className="text-lg font-bold text-purple-900">{useCase.downloads}</div>
                        <div className="text-xs text-purple-700">{t('useCases.publishedDetail.downloads')}</div>
                      </div>
                    )}
                    {useCase.shares !== undefined && (
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <Share2 className="w-5 h-5 text-green-600 mx-auto mb-1" />
                        <div className="text-lg font-bold text-green-900">{useCase.shares}</div>
                        <div className="text-xs text-green-700">{t('useCases.publishedDetail.shares')}</div>
                      </div>
                    )}
                    {useCase.likes !== undefined && (
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <Heart className="w-5 h-5 text-red-600 mx-auto mb-1" />
                        <div className="text-lg font-bold text-red-900">{useCase.likes}</div>
                        <div className="text-xs text-red-700">{t('useCases.publishedDetail.likes')}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {isPromoterSubmission && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('useCases.publishedDetail.reviewActions')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {useCase.status === 'pending_review' && (
                    <>
                      <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                        <CheckCircle className="w-5 h-5" />
                        <span>{t('useCases.publishedDetail.approve')}</span>
                      </button>
                      <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                        <AlertTriangle className="w-5 h-5" />
                        <span>{t('useCases.publishedDetail.reject')}</span>
                      </button>
                    </>
                  )}

                  {useCase.status === 'approved' && (
                    <button
                      onClick={handlePublish}
                      disabled={isPublishing}
                      className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPublishing ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>{t('useCases.publishedDetail.publishing')}</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          <span>{t('useCases.publishedDetail.publish')}</span>
                        </>
                      )}
                    </button>
                  )}

                  {useCase.status === 'published' && !useCase.featured && (
                    <button
                      onClick={handleMakeFeatured}
                      disabled={isMakingFeatured}
                      className="flex items-center justify-center space-x-2 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isMakingFeatured ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>{t('useCases.publishedDetail.featuring')}</span>
                        </>
                      ) : (
                        <>
                          <Star className="w-5 h-5" />
                          <span>{t('useCases.publishedDetail.makeFeatured')}</span>
                        </>
                      )}
                    </button>
                  )}

                  {['pending_review', 'approved'].includes(useCase.status) && (
                    <button
                      onClick={handleRequestEdit}
                      disabled={isRequestingEdit}
                      className="flex items-center justify-center space-x-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRequestingEdit ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>{t('useCases.publishedDetail.requesting')}</span>
                        </>
                      ) : (
                        <>
                          <Edit className="w-5 h-5" />
                          <span>{t('useCases.publishedDetail.requestEdit')}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {useCase.notes && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('useCases.publishedDetail.internalNotes')}</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <MessageSquare className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-yellow-800">{useCase.notes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishedUseCaseDetails;
